import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Space,
  Tag,
  Typography,
  App,
  Modal,
  Form,
  Input,
  Switch,
  Descriptions,
  Spin,
  Popconfirm,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ForkOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetRecipesQuery,
  useGetRecipeCoverageQuery,
  useCreateRecipeMutation,
  useUpdateRecipeMutation,
  useDeleteRecipeMutation,
  useGetRecipeCostQuery,
} from '../../services/recipesApi';
import { useGetProductsQuery } from '../../services/productsApi';
import type {
  Recipe,
  RecipeItem,
  CreateRecipeRequest,
  UpdateRecipeRequest,
  CreateRecipeItemRequest,
  VariantCost,
} from '../../types/inventory';
import { useResponsive } from '../../hooks/useResponsive';
import { getErrorMessage } from '../../utils/errorHelpers';
import { formatUnitCost } from '../../utils/currencyHelpers';
import VariantRecipeConfigModal from './VariantRecipeConfigModal';
import CostSimulator from './CostSimulator';
import { RecipeIngredientsEditor } from './RecipeIngredientsEditor';

const { Text } = Typography;

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
};


// =====================================================
// View Cost Modal
// =====================================================

interface ViewCostModalProps {
  recipeId: string | null;
  productId: string | null;
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const ViewCostModal: React.FC<ViewCostModalProps> = ({ recipeId, productId, open, onClose, isMobile }) => {
  const { data: costData, isLoading } = useGetRecipeCostQuery(recipeId!, {
    skip: !recipeId,
  });

  const getProfitColor = (margin: number): string => {
    if (margin > 30) return '#52c41a';
    if (margin >= 15) return '#faad14';
    return '#f5222d';
  };

  const ingredientColumns: ColumnsType<RecipeItem> = [
    {
      title: 'Ingrediente',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      align: 'right',
    },
    {
      title: 'Unidad',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
    },
    {
      title: 'Costo/Unidad',
      dataIndex: 'costPerUnit',
      key: 'costPerUnit',
      width: 120,
      align: 'right',
      render: (value: number) => formatUnitCost(value),
    },
    {
      title: 'Costo Total',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: 120,
      align: 'right',
      render: (value: number) => formatCurrency(value),
    },
  ];

  const mobileIngredientColumns: ColumnsType<RecipeItem> = [
    {
      title: 'Ingrediente',
      key: 'ingredient',
      render: (_, record) => (
        <div style={{ padding: '4px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong>{record.itemName}</Text>
            <Text>{formatCurrency(record.totalCost)}</Text>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {record.quantity} {record.unit} x {formatUnitCost(record.costPerUnit)}
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Desglose de Costo"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={isMobile ? '95vw' : 700}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : costData ? (
        <>
          <Descriptions
            column={isMobile ? 1 : 2}
            bordered
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Producto">{costData.productName}</Descriptions.Item>
            <Descriptions.Item label="Precio de Venta">
              {formatCurrency(costData.productPrice)}
            </Descriptions.Item>
            <Descriptions.Item label="Costo Total">
              {formatCurrency(costData.totalCost)}
            </Descriptions.Item>
            <Descriptions.Item label="Margen de Ganancia">
              <Text
                strong
                style={{ color: getProfitColor(costData.profitMargin), fontSize: 16 }}
              >
                {costData.profitMargin.toFixed(1)}%
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Ingredientes
          </Text>
          <Table
            rowKey="id"
            columns={isMobile ? mobileIngredientColumns : ingredientColumns}
            dataSource={costData.ingredients}
            size="small"
            pagination={false}
            scroll={isMobile ? undefined : { x: 500 }}
          />

          {costData.variantCosts && costData.variantCosts.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                Costo extra por modificador
              </Text>
              <Table<VariantCost>
                rowKey="variantId"
                columns={[
                  { title: 'Grupo', dataIndex: 'groupName', key: 'groupName', ellipsis: true },
                  { title: 'Modificador', dataIndex: 'variantName', key: 'variantName', ellipsis: true },
                  {
                    title: 'Costo Extra',
                    dataIndex: 'extraCost',
                    key: 'extraCost',
                    width: 120,
                    align: 'right',
                    render: (value: number) => formatCurrency(value),
                  },
                ]}
                dataSource={costData.variantCosts}
                size="small"
                pagination={false}
              />
            </>
          )}

          {productId && <CostSimulator productId={productId} isMobile={isMobile} />}
        </>
      ) : null}
    </Modal>
  );
};

// =====================================================
// Coverage panel (asistente de arranque 80/20)
// =====================================================

interface CoveragePanelProps {
  onCreateRecipe: (productId: string) => void;
  onActivateRecipe: (productId: string) => void;
  isMobile: boolean;
}

const CoveragePanel: React.FC<CoveragePanelProps> = ({
  onCreateRecipe,
  onActivateRecipe,
  isMobile,
}) => {
  const { data: coverage } = useGetRecipeCoverageQuery();

  if (!coverage || coverage.productsWithSales === 0) return null;

  const uncovered = coverage.products
    .filter((p) => !p.hasRecipe && !p.hasDirectLink)
    .slice(0, 5);
  const percent = Math.round(coverage.coveragePercent);

  return (
    <Card size="small" style={{ marginBottom: isMobile ? 12 : 16 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 24,
        }}
      >
        <div style={{ flex: '0 0 auto', minWidth: isMobile ? undefined : 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Progress type="circle" size={64} percent={percent} />
            <div>
              <Text strong style={{ display: 'block' }}>
                Cobertura de costos
              </Text>
              <Text style={{ display: 'block' }}>
                de tus ventas de los últimos {coverage.days} días con receta
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {coverage.productsCovered} de {coverage.productsWithSales} productos vendidos
              </Text>
            </div>
          </div>
        </div>

        {uncovered.length > 0 && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Empieza por lo que más vendes
            </Text>
            {uncovered.map((p) => (
              <div
                key={p.productId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 0',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Text ellipsis style={{ display: 'block' }}>
                    {p.productName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatCurrency(p.revenue)} vendidos · {p.revenueShare.toFixed(1)}% de tus
                    ventas
                  </Text>
                </div>
                {/* Con receta inactiva no se puede crear otra: el backend
                    rechaza cualquier receta existente, hay que reactivarla */}
                {p.hasInactiveRecipe ? (
                  <Button size="small" onClick={() => onActivateRecipe(p.productId)}>
                    Activar receta
                  </Button>
                ) : (
                  <Button size="small" onClick={() => onCreateRecipe(p.productId)}>
                    Crear receta
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

// =====================================================
// Create/Edit Recipe Modal
// =====================================================

interface RecipeFormModalProps {
  open: boolean;
  recipe: Recipe | null;
  initialProductId?: string | null;
  onClose: () => void;
  isMobile: boolean;
}

const RecipeFormModal: React.FC<RecipeFormModalProps> = ({
  open,
  recipe,
  initialProductId,
  onClose,
  isMobile,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { data: products, isLoading: loadingProducts } = useGetProductsQuery({ availableOnly: false });

  const [createRecipe, { isLoading: creating }] = useCreateRecipeMutation();
  const [updateRecipe, { isLoading: updating }] = useUpdateRecipeMutation();

  const isEditing = !!recipe;
  const isSubmitting = creating || updating;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const items: CreateRecipeItemRequest[] = (values.items || []).map(
        (item: { inventoryItemId: string; quantity: number; unit: string }) => ({
          inventoryItemId: item.inventoryItemId,
          quantity: item.quantity,
          unit: item.unit,
        })
      );

      if (items.length === 0) {
        message.warning('Agrega al menos un ingrediente');
        return;
      }

      if (isEditing && recipe) {
        const updateData: UpdateRecipeRequest = {
          notes: values.notes,
          isActive: values.isActive,
          items,
        };
        await updateRecipe({ id: recipe.id, data: updateData }).unwrap();
        message.success('Receta actualizada correctamente');
      } else {
        const createData: CreateRecipeRequest = {
          productId: values.productId,
          notes: values.notes,
          isActive: values.isActive,
          items,
        };
        await createRecipe(createData).unwrap();
        message.success('Receta creada correctamente');
      }

      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error(getErrorMessage(error));
    }
  };

  return (
    <Modal
      title={isEditing ? 'Editar Receta' : 'Nueva Receta'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEditing ? 'Guardar Cambios' : 'Crear Receta'}
      cancelText="Cancelar"
      confirmLoading={isSubmitting}
      width={isMobile ? '95vw' : 750}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          recipe
            ? {
                productId: recipe.productId,
                notes: recipe.notes,
                isActive: recipe.isActive,
                items: recipe.items.map((item) => ({
                  inventoryItemId: item.inventoryItemId,
                  quantity: item.quantity,
                  unit: item.unit,
                })),
              }
            : {
                productId: initialProductId ?? undefined,
                isActive: true,
                items: [{ inventoryItemId: undefined, quantity: undefined, unit: undefined }],
              }
        }
      >
        <Form.Item
          name="productId"
          label="Producto"
          rules={[{ required: true, message: 'Selecciona un producto' }]}
        >
          <Select
            showSearch
            placeholder="Buscar producto..."
            loading={loadingProducts}
            disabled={isEditing}
            optionFilterProp="label"
            options={products?.map((p) => ({
              value: p.id,
              label: `${p.name} - ${formatCurrency(p.price)}`,
            }))}
          />
        </Form.Item>

        <Form.Item name="notes" label="Notas">
          <Input.TextArea rows={2} placeholder="Notas opcionales sobre la receta..." />
        </Form.Item>

        <Form.Item name="isActive" label="Activa" valuePropName="checked">
          <Switch checkedChildren="Si" unCheckedChildren="No" />
        </Form.Item>

        <RecipeIngredientsEditor form={form} isMobile={isMobile} />
      </Form>
    </Modal>
  );
};

// =====================================================
// Main RecipesTab Component
// =====================================================

const RecipesTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message, modal } = App.useApp();

  const { data: recipes, isLoading } = useGetRecipesQuery();
  const [deleteRecipe] = useDeleteRecipeMutation();

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [initialProductId, setInitialProductId] = useState<string | null>(null);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costRecipe, setCostRecipe] = useState<{ id: string; productId: string } | null>(null);
  const [variantConfigProduct, setVariantConfigProduct] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = () => {
    setEditingRecipe(null);
    setInitialProductId(null);
    setFormModalOpen(true);
  };

  const handleCreateForProduct = (productId: string) => {
    setEditingRecipe(null);
    setInitialProductId(productId);
    setFormModalOpen(true);
  };

  const handleEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setInitialProductId(null);
    setFormModalOpen(true);
  };

  // Receta inactiva: se abre en edición para que el switch Activa la reviva
  const handleActivateRecipe = (productId: string) => {
    const existing = recipes?.find((r) => r.productId === productId);
    if (existing) handleEdit(existing);
  };

  const handleFormClose = () => {
    setFormModalOpen(false);
    setEditingRecipe(null);
    setInitialProductId(null);
  };

  const handleViewCost = (recipe: Recipe) => {
    setCostRecipe({ id: recipe.id, productId: recipe.productId });
    setCostModalOpen(true);
  };

  const handleCostClose = () => {
    setCostModalOpen(false);
    setCostRecipe(null);
  };

  const handleDelete = (recipe: Recipe) => {
    modal.confirm({
      title: 'Eliminar Receta',
      content: (
        <span>
          ¿Estás seguro de eliminar la receta de <strong>{recipe.productName}</strong>? Esta
          acción no se puede deshacer.
        </span>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteRecipe(recipe.id).unwrap();
          message.success('Receta eliminada correctamente');
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    });
  };

  // Desktop table columns
  const columns: ColumnsType<Recipe> = [
    {
      title: 'Producto',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: '# Ingredientes',
      dataIndex: 'ingredientCount',
      key: 'ingredientCount',
      width: 130,
      align: 'center',
    },
    {
      title: 'Costo Estimado',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      width: 150,
      align: 'right',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Activa',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>{isActive ? 'Activa' : 'Inactiva'}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => handleViewCost(record)}
          >
            Ver Costo
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ForkOutlined />}
            title="Modificadores e inventario"
            onClick={() => setVariantConfigProduct({ id: record.productId, name: record.productName })}
          />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Eliminar receta"
            description={`¿Eliminar la receta de ${record.productName}?`}
            onConfirm={async () => {
              try {
                await deleteRecipe(record.id).unwrap();
                message.success('Receta eliminada correctamente');
              } catch (error) {
                message.error(getErrorMessage(error));
              }
            }}
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Mobile card renderer
  const renderMobileCard = (recipe: Recipe) => (
    <Card
      key={recipe.id}
      size="small"
      style={{ marginBottom: 8 }}
      actions={[
        <Button
          key="cost"
          type="text"
          size="small"
          icon={<DollarOutlined />}
          onClick={() => handleViewCost(recipe)}
        >
          Costo
        </Button>,
        <Button
          key="variants"
          type="text"
          size="small"
          icon={<ForkOutlined />}
          onClick={() => setVariantConfigProduct({ id: recipe.productId, name: recipe.productName })}
        >
          Modif.
        </Button>,
        <Button
          key="edit"
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(recipe)}
        >
          Editar
        </Button>,
        <Button
          key="delete"
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(recipe)}
        >
          Eliminar
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Text strong>{recipe.productName}</Text>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {recipe.ingredientCount} ingrediente{recipe.ingredientCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text strong>{formatCurrency(recipe.estimatedCost)}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={recipe.isActive ? 'green' : 'default'}>
              {recipe.isActive ? 'Activa' : 'Inactiva'}
            </Tag>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? 12 : 16,
        }}
      >
        <div>
          <Typography.Title level={isMobile ? 5 : 4} style={{ marginBottom: 0 }}>
            Recetas
          </Typography.Title>
          {!isMobile && (
            <Text type="secondary">
              Vincula productos del menú con ingredientes del inventario para calcular costos.
            </Text>
          )}
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size={isMobile ? 'middle' : 'large'}>
          {isMobile ? 'Nueva' : 'Nueva Receta'}
        </Button>
      </div>

      <CoveragePanel
        onCreateRecipe={handleCreateForProduct}
        onActivateRecipe={handleActivateRecipe}
        isMobile={isMobile}
      />

      {isMobile ? (
        isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          recipes?.map(renderMobileCard)
        )
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={recipes || []}
          loading={isLoading}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 700 }}
        />
      )}

      <RecipeFormModal
        open={formModalOpen}
        recipe={editingRecipe}
        initialProductId={initialProductId}
        onClose={handleFormClose}
        isMobile={isMobile}
      />

      <ViewCostModal
        recipeId={costRecipe?.id ?? null}
        productId={costRecipe?.productId ?? null}
        open={costModalOpen}
        onClose={handleCostClose}
        isMobile={isMobile}
      />

      <VariantRecipeConfigModal
        open={!!variantConfigProduct}
        productId={variantConfigProduct?.id ?? null}
        productName={variantConfigProduct?.name ?? ''}
        onClose={() => setVariantConfigProduct(null)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default RecipesTab;
